import * as React from "react";
import { Alert, Input, Modal, notification } from "antd";
import { observer } from "mobx-react-lite";
import { request } from "../../../services/cpln";
import {
  ShrinkVolumeReducerState,
  RunShrinkVolumeReducerActionType,
  useRunShrinkVolumeReducer,
} from "./reducer_shrinkVolume";
import { VolumeSetMobx } from "../../../mst/kinds/volumeset";
import { NGButton } from "../../../newcomponents/button/Button";
import { NGFormData } from "../../../mobxStores/ngFormData";
import { NGFormContext } from "../../../reactContexts/ngFormContext";
import { NGFormLabel } from "../../../newcomponents/text/formLabel";
import { NGSelect } from "../../../newcomponents/select/ngselect";
import { NGInput } from "../../../newcomponents/input/input";

interface Props {
  volumeSet: VolumeSetMobx;
  initialLocation?: string;
  initialVolumeIndex?: string;
  initialStorageCapacity?: number;
  initialTimeoutSeconds?: number;
  onClose: any;
  onDone: any;
  visible: boolean;
}

const CommandModal_ShrinkVolumeRaw: React.FC<Props> = ({
  volumeSet,
  initialLocation,
  initialVolumeIndex,
  initialStorageCapacity,
  initialTimeoutSeconds,
  onClose,
  onDone,
  visible,
}) => {
  const formDataRef = React.useRef(new NGFormData());
  const [confirmationText, setConfirmationText] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const [state, dispatch] = useRunShrinkVolumeReducer(
    initialLocation,
    initialVolumeIndex,
    initialStorageCapacity ? String(initialStorageCapacity) : undefined,
    initialTimeoutSeconds ? String(initialTimeoutSeconds) : undefined,
  );

  const CONFIRMATION_TOKEN = React.useMemo(() => {
    return `${volumeSet.name}-${state.location}-${state.volumeIndex}`;
  }, [volumeSet.name, state.location, state.volumeIndex]);

  async function handleConfirm() {
    try {
      setIsLoading(true);

      const spec: ShrinkVolumeReducerState = JSON.parse(JSON.stringify(state));
      // TODO remove below after fixing number input
      spec.newStorageCapacity = Number(spec.newStorageCapacity) as any;
      spec.timeoutSeconds = Number(spec.timeoutSeconds) as any;

      await request({
        method: "post",
        url: `${volumeSet.selfLink}/-command`,
        body: {
          type: "shrinkVolume",
          spec: spec,
        },
      });
      onDone();
      notification.success({
        message: "Success",
        description: "Created Command.",
      });
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  let isValid = true;
  if (!state.location) isValid = false;
  if (!state.volumeIndex) isValid = false;
  if (!state.newStorageCapacity) isValid = false;
  if (!state.timeoutSeconds) isValid = false;
  if (confirmationText !== CONFIRMATION_TOKEN) isValid = false;

  return (
    <NGFormContext.Provider value={formDataRef.current}>
      <Modal
        width={450}
        open={visible}
        closable={false}
        title="Shrink Volume"
        footer={
          <div className="modal-actions">
            <NGButton
              disabled={isLoading}
              onClick={() => {
                onClose();
              }}
              variant={"secondary"}
              outlined
            >
              Cancel
            </NGButton>
            <NGButton loading={isLoading} disabled={isLoading || !isValid} onClick={handleConfirm} variant={"danger"}>
              Shrink
            </NGButton>
          </div>
        }
        onCancel={onClose}
        maskClosable={!isLoading}
        destroyOnClose
      >
        <Alert
          className="mb-4"
          type="warning"
          message="This will cause data loss because the volume will be replaced. Please confirm by typing the volumeset name below."
        />
        <NGFormLabel name={"location"} required invalid={!state.location}>
          Location
        </NGFormLabel>
        <NGSelect
          className="mb-4"
          name={"location"}
          value={state.location}
          invalid={!state.location}
          onChange={(value) => dispatch({ type: RunShrinkVolumeReducerActionType.LOCATION, payload: value })}
          options={volumeSet.status.locations.map(({ name: l }) => ({
            label: l,
            value: l,
            disabled: false,
            isLabel: false,
          }))}
        />
        <NGFormLabel name={"volumeIndex"} required invalid={state.volumeIndex.length < 1}>
          Volume Index
        </NGFormLabel>
        <NGInput
          className="mb-4"
          name={"volumeIndex"}
          value={state.volumeIndex}
          invalid={state.volumeIndex.length < 1}
          onChange={(e) => dispatch({ type: RunShrinkVolumeReducerActionType.INDEX, payload: e.target.value })}
        />
        <NGFormLabel name={"storageCapacity"} required invalid={state.newStorageCapacity.length < 1}>
          New Storage Capacity (GiB)
        </NGFormLabel>
        <NGInput
          className="mb-4"
          name={"storageCapacity"}
          value={state.newStorageCapacity}
          invalid={state.newStorageCapacity.length < 1}
          onChange={(e) =>
            dispatch({ type: RunShrinkVolumeReducerActionType.NEW_STORAGE_CAPACITY, payload: e.target.value })
          }
        />
        <NGFormLabel name={"timeoutSeconds"} required invalid={state.timeoutSeconds.length < 1}>
          Timeout Seconds
        </NGFormLabel>
        <NGInput
          className="mb-4"
          name={"timeoutSeconds"}
          value={state.timeoutSeconds}
          invalid={state.timeoutSeconds.length < 1}
          onChange={(e) =>
            dispatch({ type: RunShrinkVolumeReducerActionType.TIMEOUT_SECONDS, payload: e.target.value })
          }
        />
        <div className="mt-4">{`Type "${CONFIRMATION_TOKEN}" to confirm`}</div>
        <Input
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder={`confirmation`}
        />
      </Modal>
    </NGFormContext.Provider>
  );
};

export const CommandModal_ShrinkVolume = observer(CommandModal_ShrinkVolumeRaw);
